import type { OverDetails } from "@geoblocks/elevation-profile";
import ElevationProfileElement from "@geoblocks/elevation-profile";
import type { ScaleLinear } from "d3-scale";
import type { TemplateResult } from "lit-html";
import { svg } from "lit-html";
import { distance as getDistance } from "ol/coordinate";
import Feature from "ol/Feature";
import type LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import VectorLayer from "ol/layer/Vector";
import type OLMap from "ol/Map";
import VectorSource from "ol/source/Vector";
import { mergeSegmentDetails, type Triplet, toTriplet } from "./api-hiking";
import { indexToLabel, toGlobalIndex } from "./utils";

export type HikingProfile = {
  lines: number[][][];
  points: number[][];
  lineSegments: Triplet[];
  xAxisSegments: Triplet[];
};

export type ElevationProfileOptions = {
  /**
   * The target element for the elevation profile
   */
  target: HTMLElement | string;
  /**
   * An OpenLayers map instance
   */
  map: OLMap;
  /**
   * Callback for when the mouse is over the elevation profile
   */
  over?: (event: CustomEvent<OverDetails>) => void;
  /**
   * Callback for when the mouse leaves the elevation profile
   */
  out?: () => void;
};

/**
 * # ElevationProfile
 *
 * ## Demo
 * - [ElevationProfile](/demo/elevation-profile.html)
 *
 * ## Description
 *
 * The ElevationProfile is responsible for displaying the elevation profile of a hiking and its stopovers.
 *
 * To use the ElevationProfile, you need to set the target, the element where to display to profile, and the map.
 * ```js
 * const profile = new sww.ElevationProfile({
 *   target: "profile",
 *   map: map,
 * });
 * ```
 * Where `profile` is the id of the element where to display the profile and `map` is an OpenLayers map instance.
 *
 * Then, the profile needs to be linked to the HikingEditor to the hiking editor to be updated when the hiking is changes:
 * ```js
 * const editor = new sww.HikingEditor();
 *
 * editor.addEventListener("change", () => {
 *   const { lines, points, lineSegments, xAxisSegments } = editor.getProfile();
 *   profile.setProfile(lines, points, lineSegments, xAxisSegments);
 * });
 * ```
 *
 * ## Events callbacks
 *
 * The `over` and `out` callbacks are called when the mouse is over the elevation profile and when it leaves the elevation profile, respectively.
 *
 * They receive a CustomEvent with the following details:
 * - `coordinate`: the coordinate of the point on the profile that is hovered (`[x, y, elevation, distance]`)
 * - `position`: the position in pixels of the mouse relative to the component ({x: number, y: number})
 * - `segments`: values of the line and xAxis at the hovered point (`{ line: string, xAxis: string }`)
 *
 * ## Styling
 *
 * The `sww.css` file must be included in the page to style the elevation profile.
 *
 */
export default class ElevationProfile {
  private profile: ElevationProfileElement;
  private highlightPoint: Point;
  private highlightLayer: VectorLayer<Feature>;
  private yRange: [number, number] | null = null;
  private yBounds: [number, number] | null = null;

  constructor(options: ElevationProfileOptions) {
    this.profile = new ElevationProfileElement();

    const target = typeof options.target === "string" ? document.getElementById(options.target) : options.target;

    if (!target) {
      throw new Error("ElevationProfile: target not found");
    }
    if (!options.map) {
      throw new Error("ElevationProfile: map not found");
    }
    if (options.over) {
      this.profile.addEventListener("over", options.over as EventListener);
    }
    if (options.out) {
      this.profile.addEventListener("out", options.out as EventListener);
    }

    this.highlightPoint = new Point([]);
    this.highlightLayer = new VectorLayer<Feature>({
      style: {
        "circle-radius": 8,
        "circle-fill-color": "#F2000C",
        "circle-stroke-color": "#fff",
        "circle-stroke-width": 3,
      },
      source: new VectorSource<Feature>({
        features: [new Feature(this.highlightPoint)],
      }),
    });
    this.highlightLayer.setMap(options.map);

    this.profile.tickFormat = this.tickFormat.bind(this);
    this.profile.pointSvg = this.pointSvg.bind(this);

    this.profile.updateScale = this.updateScale.bind(this);

    this.profile.addEventListener("over", this.onElevationProfileOver.bind(this) as EventListener);
    this.profile.addEventListener("out", this.onElevationProfileOut.bind(this));

    this.profile.margin = { top: 20, right: 40, bottom: 40, left: 60 };
    target.appendChild(this.profile);
  }

  public async setProfile(lines: number[][][], points: number[][], lineSegments: Triplet[], xAxisSegments: Triplet[]) {
    const elevations = lines.flat().map((line) => line[2]);
    this.yRange = [Math.min(...elevations), Math.max(...elevations)];
    this.profile.lines = lines;
    this.profile.points = points;
    this.profile.lineSegments = lineSegments;
    this.profile.xAxisSegments = xAxisSegments;
    await this.profile.updateComplete;
    this.hideNotUsedAxisPart();
  }

  private tickFormat(value: number, _axis: "x" | "y"): string {
    return value.toString();
  }

  getTickValues(axisHeight: number, yDomainLength: number, yTickSize = 40): number[] {
    if (!this.yRange?.length) return [];
    const numTicks = Math.ceil(axisHeight / yTickSize);
    const [min, max] = this.yRange;
    const values: number[] = [];

    const yMean = (max + min) / 2;
    const yHalfDomain = yDomainLength / 2;
    let rawLowerBound = yMean - yHalfDomain;
    let rawUpperBound = yMean + yHalfDomain;

    // Shift domain if lower bound is negative
    if (rawLowerBound < 0) {
      const shift = 0 - rawLowerBound;
      rawLowerBound = 0;
      rawUpperBound += shift;
    }

    const rawBoundsRange = rawUpperBound - rawLowerBound;
    const rawStep = rawBoundsRange / numTicks;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const residual = rawStep / magnitude;
    let step = 10;
    if (residual < 1.5) step = 1;
    else if (residual < 3) step = 2;
    else if (residual < 7) step = 5;
    step *= magnitude;

    let lowerBound = step * Math.round(rawLowerBound / step);
    let upperBound = Math.round(rawUpperBound + (lowerBound - rawLowerBound));

    // Shift domain if lowest point is out of plot
    if (min < lowerBound) {
      const shift = step * Math.ceil((lowerBound - min) / step);
      lowerBound -= shift;
      upperBound -= shift;
    }

    for (let i = 0; i <= numTicks + 1; i++) {
      let value = lowerBound + i * step;
      if (step < 5) {
        const decimalPart = value % 5;
        value = decimalPart >= 2.5 ? Math.ceil(value / 5) * 5 : Math.floor(value / 5) * 5;
      }

      if (value <= upperBound) {
        values.push(value);
      } else {
        // Ensure the last value does not exceed the max
        values.push(upperBound);
        break;
      }
    }

    if (values.length > 1 && values[values.length - 1] > upperBound) {
      values[values.length - 1] = upperBound;
    }

    // Reduce plot size by removing ticks that far from the min point for more than one step
    if (min - lowerBound > step) {
      const newLowerBound = values.reduce((closest, num) => {
        return num < min && min - num < min - closest ? num : closest;
      }, lowerBound);

      const shift = newLowerBound - lowerBound;
      values[0] = newLowerBound;
      for (let i = 1; i < values.length; i++) {
        values[i] += shift;
      }
    }

    return values;
  }

  private pointSvg(x: number, y: number, index: number): TemplateResult {
    const points = this.profile.points[index];
    const first = index === 0;
    const last = index === this.profile.points.length - 1;
    const profileBottom = this.profile.offsetHeight - this.profile.margin.bottom;
    const axisLabel = (points[3] / 1000).toFixed(1) + (last ? " km" : "");

    let circleCenter: TemplateResult;

    if (first || last) {
      circleCenter = svg`<circle class="${`stopover-${first ? "first" : "last"}`}" cx="${x}" cy="${y}" r="8" />`;
    } else {
      circleCenter = svg`<text class="stopover-text" x="${x}" y="${y}" dy=".4em">${indexToLabel(index)}</text>`;
    }

    return svg`
      <g>
        <line class="stopover-line" x1="${x}" y1="${y}" x2="${x}" y2="${profileBottom + 6}"/>
        <circle class="stopover-circle" cx="${x}" cy="${y}" r="12" />
        ${circleCenter}
        <text class="stopover-axis" x="${x}" y="${profileBottom + 24}" dy=".3em">${axisLabel}</text>
      </g>
    `;
  }

  private onElevationProfileOver(event: CustomEvent<OverDetails>) {
    this.highlightPoint.setCoordinates(event.detail.coordinate);
  }

  private onElevationProfileOut() {
    this.highlightPoint.setCoordinates([]);
  }

  private updateScale(
    scaleX: ScaleLinear<number, number>,
    scaleY: ScaleLinear<number, number>,
    _width: number,
    height: number,
  ) {
    const ratioXY = 0.5;

    const xDomain = scaleX.domain();

    const xMin = xDomain[0];
    const xMax = xDomain[1];

    const yDomain = scaleY.domain();
    const yMax = yDomain[1];

    const margin = this.profile.margin;
    const axisHeight = height - margin.top - margin.bottom;
    let yDomainLength: number | undefined;
    let tickValues: number[] | undefined;

    const width_scales = ["100%", "90%", "80%", "70%", "60%", "50%", "40%", "30%", "20%", "10%"];
    for (const width_scale of width_scales) {
      this.profile.style.width = width_scale;
      const axisWidth = this.profile.offsetWidth - margin.left - margin.right;
      const xResolution = (xMax - xMin) / axisWidth;
      yDomainLength = ratioXY * xResolution * axisHeight;

      tickValues = this.getTickValues(axisHeight, yDomainLength);
      const step = tickValues[1] - tickValues[0];
      const lastTick = tickValues[tickValues.length - 1];
      const secondLastTickValue = tickValues[tickValues.length - 2];
      const lastTickIsNice = lastTick % step === 0;

      // To have nice ticks, either the last tick is multiple of the step
      // or the second last tick (that is always nice)is greater than the max, in which case the last tick will be hidden afterward
      if ((lastTickIsNice && lastTick > yMax) || secondLastTickValue > yMax) break;
    }

    // set y domain and tick values
    if (yDomainLength && tickValues) {
      this.yBounds = [tickValues[0], tickValues[tickValues.length - 1]];
      scaleY.domain(this.yBounds);
      this.profile.tickValues(tickValues, "y");
    }
  }

  hideNotUsedAxisPart() {
    const yAxis = this.profile.querySelector(".y.axis");
    if (yAxis && this.yRange?.length) {
      const yMax = this.yRange[1];
      let lastTickValue: number | undefined;
      const ticks = yAxis.querySelectorAll(".tick");
      for (let i = 0; i < ticks.length; i++) {
        const tick = ticks[i];
        const value = Number(tick.querySelector("text")?.textContent);
        const tickStyle = (<HTMLElement>tick).style;
        if (yMax < value && lastTickValue && lastTickValue !== value) {
          tickStyle.display = "none";
        } else if (yMax < value) {
          lastTickValue = value;
          tickStyle.display = "block";
        }
      }

      if (lastTickValue && this.yBounds) {
        const yLowerBound = this.yBounds[0];
        const yUpperBound = this.yBounds[1];
        const scale = (lastTickValue - yLowerBound) / (yUpperBound - yLowerBound);
        const domain = <SVGAElement>yAxis.querySelector(".domain");
        const bbox = domain.getBBox();
        domain.style.transformOrigin = `50% ${bbox.y + bbox.height}px`;
        domain.setAttribute("transform", `scale(1,${scale})`);
      }
    }
  }
}

export function getProfile(segments: Feature<LineString>[][], stopoverIndexes: number[][]): HikingProfile {
  // remove empty segments
  const cleanSegments = segments.filter((s) => s.length > 0);

  // Remove corresponding stopover indexes
  const emptySegmentsIndexes = segments.map((s, i) => (s.length === 0 ? i : -1)).filter((i) => i !== -1);
  const cleanStopoverIndexes = stopoverIndexes.filter((index) => !emptySegmentsIndexes.includes(index[0]));

  // renumber stopover parts to match clean segments
  let currentPartIndex = -1;
  let prevPartIndex = -1;
  for (let i = 0; i < cleanStopoverIndexes.length; i++) {
    if (cleanStopoverIndexes[i][0] !== prevPartIndex) {
      currentPartIndex++;
    }
    prevPartIndex = cleanStopoverIndexes[i][0];
    cleanStopoverIndexes[i][0] = currentPartIndex;
  }

  const lines = [];
  const points = [];
  const surfaces = [];
  const structures = [];
  const hikingCategories = [];
  const linePointCounts = []; // Track number of points per line for gap handling
  let distance = 0;
  let prevEndPoint = null;
  for (const part of cleanSegments) {
    if (prevEndPoint) {
      // @ts-expect-error: we remove empty segments beforehand
      const nextFirstPoint = part[0].getGeometry().getFirstCoordinate();
      distance += getDistance(prevEndPoint, nextFirstPoint);
    }
    const line = [];
    const data = mergeSegmentDetails(part);
    surfaces.push(data.surfaces);
    structures.push(data.structures);
    hikingCategories.push(data.categories);

    for (let i = 0; i < part.length; i++) {
      const segment = part[i];
      let profile = segment.get("profile").map((c: number[]) => [c[0], c[1], c[2], c[3] + distance]);

      // Add the start point of the segment to the points list
      // This corresponds to the control point at this index
      points.push(profile[0]);

      if (i > 0) {
        // Skip the first point as it is the same as the last point of the previous segment
        profile = profile.slice(1);
      }

      line.push(...profile);
      distance = profile.at(-1).at(3);
    }
    prevEndPoint = line.at(-1);
    points.push(prevEndPoint);
    lines.push(line);
    linePointCounts.push(line.length); // Store the number of points in this line
  }

  const stopovers = [];
  // points array has one entry per CP (segments + 1), so point counts are used here.
  const pointsByPart = cleanSegments.map((s) => s.length + 1);
  for (const [part, index] of cleanStopoverIndexes) {
    const pointIndex = toGlobalIndex(pointsByPart, part, index);
    const point = points[pointIndex];
    if (point) {
      stopovers.push(points[pointIndex]);
    }
  }

  const flattenedSurfaces = flattenDetails(surfaces, linePointCounts).map(toTriplet);
  const flattenedStructures = flattenDetails(structures, linePointCounts).map(toTriplet);
  const flattenedHikingCategories = flattenDetails(hikingCategories, linePointCounts).map(toTriplet);

  const combinedDetails = combineDetails(flattenedSurfaces, flattenedStructures);

  return {
    lines: lines,
    points: stopovers,
    lineSegments: combinedDetails,
    xAxisSegments: flattenedHikingCategories,
  };
}

function flattenDetails(
  detailss: { start: number; end: number; type: string }[][],
  linePointCounts?: number[],
): { start: number; end: number; type: string }[] {
  const flattened: { start: number; end: number; type: string }[] = [];
  let offset = 0;

  for (let i = 0; i < detailss.length; i++) {
    const details = detailss[i];
    for (const detail of details) {
      flattened.push({
        start: detail.start + offset,
        end: detail.end + offset,
        type: detail.type,
      });
    }
    // Calculate offset for next line: current line length + 1 (for gap between lines)
    if (linePointCounts && linePointCounts[i]) {
      offset += linePointCounts[i] + (i < linePointCounts.length - 1 ? 1 : 0);
    } else if (details.length > 0) {
      // Fallback for single line case
      offset += details[details.length - 1].end;
    }
  }

  return flattened;
}

function combineDetails(surfaces: Triplet[], structures: Triplet[]): Triplet[] {
  const combined: Triplet[] = [];
  let i = 0;
  let j = 0;

  while (i < surfaces.length && j < structures.length) {
    const [surfStart, surfEnd, surfType] = surfaces[i];
    const [structStart, structEnd, structType] = structures[j];

    const start = Math.max(surfStart, structStart);
    const end = Math.min(surfEnd, structEnd);

    if (start < end) {
      let type = "other";
      if (/bruecke|steg/i.test(structType)) {
        type = "aboveground";
      } else if (/treppe/i.test(structType)) {
        type = "stairs";
      } else if (/tunnel|unterfuehrung|galerie/i.test(structType)) {
        type = "underground";
      } else if (surfType === "paved") {
        type = "paved";
      } else if (surfType === "unpaved") {
        type = "unpaved";
      }

      combined.push([start, end, type]);
    }

    if (surfEnd < structEnd) {
      i++;
    } else if (surfEnd > structEnd) {
      j++;
    } else {
      i++;
      j++;
    }
  }

  return combined;
}
